import { useCallback, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/src/store";
import { useNavigate } from "react-router-dom";
import { admissionDatabaseRequests } from "@/src/api/admissionOfficeDatabaseRequests";
import { applicantGenerateDocsRequests } from "@/src/api/applicantGenerateDocsRequests";
import { applicantHistoryRequests } from "@/src/api/applicantHistoryRequests";
import { applicantsRequests } from "@/src/api/applicantsRequests";
import { tasksPageRequests } from "@/src/api/tasksPageRequests";
import { studentsRequests } from "@/src/api/studentsRequests";
import { buildSimpleHistoryText, createApplicantHistory } from "@/src/utils/applicants/createApplicantHistory";
import { admissionDatabaseApi } from "@/src/api/axiosInstance";
import { tasksStatusConst } from "@/src/constants/tasksConsts";
import { usePushNotifications } from "@/src/components/CustomUtils/PushNotifications/PushNotificationsProvider";
import { setIsPreloyder } from "@/src/store/slices/Preloyder/PreloyderSlice";
import { setAdmissionFormContext } from "@/src/store/slices/AdmissionOffice/admissionOfficeDocSlice";
import { IAdmissionGeneratedDocumentResponse } from "@/src/types/admissionOfficeGeneratedDocument";
import type { ApplicantGenerateDocType } from "@/src/types/applicantGenerateDocs";
import { parseGeneratedDocumentResponse } from "@/src/utils/admissionOffice/parseGeneratedDocumentResponse";
import {
  buildApproveUpdatesFromFormDiff,
  logApproveChangedFields,
} from "@/src/utils/admissionOffice/buildApproveUpdatesFromFormDiff";
import { resolveTaskAttachedFileUrl } from "@/src/utils/admissionOffice/resolveTaskAttachedFileUrl";
import {
  readAdmissionFormAttachedFile,
  resolvePrefillTaskAttachedFile,
} from "@/src/utils/admissionOffice/resolvePrefillTaskAttachedFile";
import { resolveAndDeleteApplicantGenerateDoc } from "@/src/utils/applicantGenerateDocs/resolveAndDeleteApplicantGenerateDoc";
import { joinBaseUrlAndPath } from "@/src/utils/joinUrl";
import { executeDocumentCheckReject } from "@/src/utils/tasks/executeDocumentCheckReject";
import {
  buildTaskCreatePayloadFromAOPForm,
  parseCreatedTaskId,
} from "@/src/utils/tasks/buildTaskCreatePayloadFromAOPForm";
import type {
  AdmissionTaskPrefillFormBaseline,
  AdmissionTaskPrefillMapping,
  AdmissionTaskPrefillPayload,
} from "@/src/hooks/useAdmissionTaskPrefill";

const NEXT_STATUS = "document_check";
const IN_PROCESS_STATUS = "in_process";
const REJECTED_STATUS = "rejected";

const buildAdmissionPdfUrl = (pdfPath: string) => joinBaseUrlAndPath(admissionDatabaseApi.defaults.baseURL ?? "", pdfPath);

const buildGeneratedPdfFileName = (fullName: string, title: string) => {
  const safeName = fullName.replace(/[<>:"/\\|?*]+/g, "").trim();
  const safeTitle = title.replace(/[<>:"/\\|?*]+/g, "").trim();
  if (!safeName && !safeTitle) return "document.pdf";
  if (!safeTitle) return `${safeName}.pdf`;
  if (!safeName) return `${safeTitle}.pdf`;
  return `${safeName}(${safeTitle}).pdf`;
};

export interface UseAdmissionGeneratedDocumentActionsOptions {
  getValues: () => object;
  /** Used to write created `task_id` back into the form (Student Id → Generate path). */
  setValue?: (name: string, value: string) => void;
  /** When set, writes student history after approve (admission letter). */
  studentIdFormField?: string;
  /**
   * When set to admission/invoice, successful generate with student id + document_seria
   * also creates a CRM `/applicant-generate-docs` link.
   */
  documentType?: ApplicantGenerateDocType;
}

interface TaskPrefillSnapshot {
  payload: AdmissionTaskPrefillPayload;
  mapping: AdmissionTaskPrefillMapping[];
  formBaseline: AdmissionTaskPrefillFormBaseline;
}

interface TaskPrefillMeta {
  status?: string;
  attachedFile?: string;
  formOffice?: string;
}

export const useAdmissionGeneratedDocumentActions = ({
  getValues,
  setValue,
  studentIdFormField,
  documentType,
}: UseAdmissionGeneratedDocumentActionsOptions) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const admissionFormTaskId = useSelector((state: RootState) => state.admissionOfficeDoc.taskId);
  const admissionFormApplicantId = useSelector((state: RootState) => state.admissionOfficeDoc.applicantId);
  const { notify } = usePushNotifications();
  const taskPrefillSnapshotRef = useRef<TaskPrefillSnapshot | null>(null);
  const [taskPrefillMeta, setTaskPrefillMeta] = useState<TaskPrefillMeta | null>(null);

  const resolveStudentApplicantId = useCallback((): number | null => {
    const formValues = getValues() as Record<string, unknown>;
    const snapshot = taskPrefillSnapshotRef.current;
    const raw =
      formValues.applicant_id ??
      snapshot?.payload.applicant_id ??
      (studentIdFormField ? formValues[studentIdFormField] : undefined) ??
      formValues.student_id ??
      snapshot?.payload.student_id ??
      admissionFormApplicantId;
    const parsed = raw != null ? Number(raw) : NaN;
    return Number.isFinite(parsed) && parsed >= 1 ? parsed : null;
  }, [admissionFormApplicantId, getValues, studentIdFormField]);

  const [isGenerated, setIsGenerated] = useState(false);
  const [generatedDocument, setGeneratedDocument] = useState<IAdmissionGeneratedDocumentResponse | null>(null);
  const [isGeneratedPdfPreviewOpen, setIsGeneratedPdfPreviewOpen] = useState(false);

  const generatedPdfUrl = generatedDocument?.PDFpath ? buildAdmissionPdfUrl(generatedDocument.PDFpath) : null;

  const normalizeTaskPrefillStatus = (status?: string): string =>
    String(status ?? "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");

  const hasTaskAttachedFile = Boolean(taskPrefillMeta?.attachedFile?.trim());
  const isDocumentCheckStatus = normalizeTaskPrefillStatus(taskPrefillMeta?.status) === "document_check";

  const isTaskDocumentCheckReview = isDocumentCheckStatus && hasTaskAttachedFile;

  const taskAttachedPdfUrl = isTaskDocumentCheckReview
    ? resolveTaskAttachedFileUrl(taskPrefillMeta?.attachedFile)
    : null;

  const previewPdfUrl = generatedPdfUrl ?? taskAttachedPdfUrl;
  /** Eye icon: only when task is Document Check with an attached file. */
  const canOpenTaskDocumentEye = isTaskDocumentCheckReview && Boolean(taskAttachedPdfUrl);
  const canOpenPdfPreview = canOpenTaskDocumentEye;

  const generatedPreviewTitle = useMemo(() => {
    if (generatedDocument) {
      const fullName = generatedDocument.fullName?.trim() || "Document";
      const title = generatedDocument.title?.trim();
      return title ? `${fullName} — ${title}` : fullName;
    }

    if (isTaskDocumentCheckReview) {
      const fullName = taskPrefillSnapshotRef.current?.payload.fullName?.trim() || "Document";
      return `${fullName} — attached document`;
    }

    return "Document preview";
  }, [generatedDocument, isTaskDocumentCheckReview, taskPrefillMeta]);

  const setTaskPrefillSnapshot = useCallback(
    (
      payload: AdmissionTaskPrefillPayload,
      mapping: AdmissionTaskPrefillMapping[],
      formBaseline: AdmissionTaskPrefillFormBaseline,
    ) => {
      taskPrefillSnapshotRef.current = { payload, mapping, formBaseline };
      setTaskPrefillMeta({
        status: typeof payload.status === "string" ? payload.status : undefined,
        attachedFile: typeof payload.attached_file === "string" ? payload.attached_file : undefined,
        formOffice: typeof payload.form_office === "string" ? payload.form_office : undefined,
      });

      const taskId = String(payload.task_id ?? "").trim();
      const applicantRaw = payload.applicant_id ?? payload.student_id;
      const applicantParsed = applicantRaw != null ? Number(applicantRaw) : NaN;
      const officeUrl = String(payload.office_url ?? "").trim();
      const attachedFile = typeof payload.attached_file === "string" ? payload.attached_file.trim() : "";
      // Defer so Header's pathname clear effect does not wipe this context.
      window.setTimeout(() => {
        dispatch(
          setAdmissionFormContext({
            taskId,
            applicantId: Number.isFinite(applicantParsed) && applicantParsed > 0 ? applicantParsed : null,
            officeUrl,
            attachedFile,
          }),
        );
      }, 0);
    },
    [dispatch],
  );

  /** Clears generated PDF/preview only. Keeps task prefill snapshot for Approve diff. */
  const resetGeneratedState = useCallback(() => {
    setGeneratedDocument(null);
    setIsGenerated(false);
    setIsGeneratedPdfPreviewOpen(false);
  }, []);

  const lookupDocumentIdByPdfPath = useCallback(async (pdfPath: string): Promise<number | null> => {
    const targetPath = pdfPath.replace(/^\/+/, "");
    try {
      const listing = await admissionDatabaseRequests.getListDefault("1");
      const docs = listing?.docsArray ?? [];
      const match = docs.find((doc) => {
        if (!doc.pdf_path) return false;
        return doc.pdf_path.replace(/^\/+/, "") === targetPath;
      });
      return typeof match?.id === "number" ? match.id : null;
    } catch (error) {
      console.warn("[AdmissionOffice] Could not look up document id by pdf_path:", error);
      return null;
    }
  }, []);

  const handleDownloadGeneratedPdf = useCallback(async () => {
    const downloadUrl = isTaskDocumentCheckReview ? taskAttachedPdfUrl : generatedPdfUrl;
    if (!downloadUrl) return;

    const downloadFileName = isTaskDocumentCheckReview
      ? taskPrefillMeta?.attachedFile?.split("/").pop()?.trim() || "document.pdf"
      : generatedDocument
        ? buildGeneratedPdfFileName(generatedDocument.fullName, generatedDocument.title)
        : "document.pdf";

    dispatch(setIsPreloyder(true));
    try {
      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error("Failed to download file");

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = downloadFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("PDF download error:", error);
      notify({
        title: "Download failed",
        message: isTaskDocumentCheckReview
          ? "Could not download attached document."
          : "Could not download generated PDF.",
        type: "error",
      });
    } finally {
      dispatch(setIsPreloyder(false));
    }
  }, [
    dispatch,
    generatedDocument,
    generatedPdfUrl,
    isTaskDocumentCheckReview,
    notify,
    taskAttachedPdfUrl,
    taskPrefillMeta?.attachedFile,
  ]);

  const clearTaskPrefillState = useCallback(() => {
    taskPrefillSnapshotRef.current = null;
    setTaskPrefillMeta(null);
  }, []);

  const syncTaskPrefillAfterInProcess = useCallback(() => {
    setTaskPrefillMeta((prev) =>
      prev
        ? {
            ...prev,
            status: IN_PROCESS_STATUS,
            attachedFile: undefined,
          }
        : null,
    );

    const snapshot = taskPrefillSnapshotRef.current;
    if (!snapshot) {
      dispatch(setAdmissionFormContext({ attachedFile: "" }));
      return;
    }

    taskPrefillSnapshotRef.current = {
      ...snapshot,
      payload: {
        ...snapshot.payload,
        status: IN_PROCESS_STATUS,
        attached_file: "",
      },
    };
    dispatch(setAdmissionFormContext({ attachedFile: "" }));
  }, [dispatch]);

  const syncTaskPrefillAfterRejected = useCallback(() => {
    setTaskPrefillMeta((prev) =>
      prev
        ? {
            ...prev,
            status: REJECTED_STATUS,
          }
        : null,
    );

    const snapshot = taskPrefillSnapshotRef.current;
    if (!snapshot) return;

    taskPrefillSnapshotRef.current = {
      ...snapshot,
      payload: {
        ...snapshot.payload,
        status: REJECTED_STATUS,
      },
    };
  }, []);

  const syncTaskPrefillAfterDocumentCheckSave = useCallback((attachedFile: string) => {
    const normalizedAttachedFile = attachedFile.trim();
    setTaskPrefillMeta((prev) =>
      prev
        ? {
            ...prev,
            status: NEXT_STATUS,
            attachedFile: normalizedAttachedFile,
          }
        : null,
    );

    const snapshot = taskPrefillSnapshotRef.current;
    if (!snapshot) return;

    taskPrefillSnapshotRef.current = {
      ...snapshot,
      payload: {
        ...snapshot.payload,
        status: NEXT_STATUS,
        attached_file: normalizedAttachedFile,
      },
    };
  }, []);

  /**
   * Student Id / email prefill has no task yet. On Generate doc: create a task with form data first,
   * then the existing attach flow can bind the PDF.
   * Call ONLY when the form was loaded via Student Id or email (source: applicant).
   */
  const bindNewTaskToForm = useCallback(
    (createdTaskId: string, applicantId?: number) => {
      setValue?.("task_id", createdTaskId);

      const snapshot = taskPrefillSnapshotRef.current;
      if (snapshot) {
        taskPrefillSnapshotRef.current = {
          ...snapshot,
          payload: {
            ...snapshot.payload,
            task_id: createdTaskId,
            attached_file: "",
            status: IN_PROCESS_STATUS,
          },
          formBaseline: {
            ...snapshot.formBaseline,
            task_id: createdTaskId,
          },
        };
      }

      setTaskPrefillMeta((prev) =>
        prev
          ? {
              ...prev,
              attachedFile: undefined,
              status: IN_PROCESS_STATUS,
            }
          : null,
      );

      window.setTimeout(() => {
        dispatch(
          setAdmissionFormContext({
            taskId: createdTaskId,
            attachedFile: "",
            ...(applicantId != null && Number.isFinite(applicantId) ? { applicantId } : {}),
          }),
        );
      }, 0);
    },
    [dispatch, setValue],
  );

  const createTaskFromForm = useCallback(
    async (options?: {
      logLabel?: string;
      successTitle?: string;
      successMessage?: string;
    }): Promise<string | null> => {
      const formValues = getValues() as Record<string, unknown>;
      const snapshot = taskPrefillSnapshotRef.current;
      const applicantIdRaw =
        formValues.applicant_id ??
        snapshot?.payload.applicant_id ??
        formValues.student_id ??
        snapshot?.payload.student_id;
      const applicantId = applicantIdRaw != null ? Number(applicantIdRaw) : NaN;
      if (!Number.isFinite(applicantId) || applicantId <= 0) {
        notify({
          title: "Cannot create task",
          message: "Applicant Id is missing. Cannot create a task without an applicant.",
          type: "error",
        });
        return null;
      }

      try {
        const createPayload = buildTaskCreatePayloadFromAOPForm(formValues, applicantId);
        console.groupCollapsed(options?.logLabel ?? "[AdmissionOffice] POST /tasks/create");
        console.log(createPayload);
        console.groupEnd();

        const createResponse = await tasksPageRequests.create(createPayload);
        const createdTaskId = parseCreatedTaskId(createResponse);
        if (!createdTaskId) {
          console.error("[AdmissionOffice] Task create response missing id:", createResponse);
          notify({
            title: "Task create failed",
            message: "Task was created but id was not returned. Refresh Tasks and try again.",
            type: "error",
          });
          return null;
        }

        bindNewTaskToForm(createdTaskId, applicantId);

        notify({
          title: options?.successTitle ?? "Task created",
          message: options?.successMessage ?? `Task ${createdTaskId} created. Attaching document next.`,
          type: "success",
        });

        return createdTaskId;
      } catch (error) {
        console.error("[AdmissionOffice] Failed to create task:", error);
        notify({
          title: "Task create failed",
          message: "Could not create a task before generating the document.",
          type: "error",
        });
        return null;
      }
    },
    [bindNewTaskToForm, getValues, notify],
  );

  const ensureTaskCreatedFromStudentPrefill = useCallback(async (): Promise<boolean> => {
    const formValues = getValues() as Record<string, unknown>;
    const snapshot = taskPrefillSnapshotRef.current;
    const existingTaskId = String(
      formValues.task_id ?? snapshot?.payload.task_id ?? admissionFormTaskId ?? "",
    ).trim();
    if (existingTaskId) return true;

    const isStudentPrefill = snapshot?.payload.source === "applicant";
    if (!isStudentPrefill) {
      notify({
        title: "Cannot create task",
        message: "Task creation is only available after loading a user by Student Id or email.",
        type: "error",
      });
      return false;
    }

    const createdTaskId = await createTaskFromForm({
      logLabel: "[AdmissionOffice] POST /tasks/create (Applicant prefill → Generate)",
    });
    return createdTaskId != null;
  }, [admissionFormTaskId, createTaskFromForm, getValues, notify]);

  /**
   * Before document generate:
   * - no task_id + student prefill → create task
   * - task_id + empty attached_file → keep current task
   * - task_id + attached_file → create new task for the new document
   */
  const ensureTaskReadyForDocumentGenerate = useCallback(async (): Promise<boolean> => {
    const formValues = getValues() as Record<string, unknown>;
    const snapshot = taskPrefillSnapshotRef.current;
    const existingTaskId = String(
      formValues.task_id ?? snapshot?.payload.task_id ?? admissionFormTaskId ?? "",
    ).trim();

    if (!existingTaskId) {
      return ensureTaskCreatedFromStudentPrefill();
    }

    const attachedFile = await resolvePrefillTaskAttachedFile(existingTaskId, [
      taskPrefillMeta?.attachedFile,
      snapshot?.payload.attached_file,
      readAdmissionFormAttachedFile(),
    ]);

    if (!attachedFile) {
      return true;
    }

    const createdTaskId = await createTaskFromForm({
      logLabel: `[AdmissionOffice] POST /tasks/create (Task ${existingTaskId} already has document → new task)`,
      successTitle: "New task created",
      successMessage: `A new task was created for this document. The previous task (${existingTaskId}) keeps its existing document.`,
    });
    return createdTaskId != null;
  }, [
    admissionFormTaskId,
    createTaskFromForm,
    ensureTaskCreatedFromStudentPrefill,
    getValues,
    taskPrefillMeta?.attachedFile,
  ]);

  const markTaskInProcessAndClearAttachment = useCallback(
    async (taskId: string, applicantId?: number) => {
      await tasksPageRequests.update(taskId, {
        status: IN_PROCESS_STATUS,
        attached_file: null,
      });

      const inProcessLabel =
        tasksStatusConst.find((item) => item.id === IN_PROCESS_STATUS)?.label ?? IN_PROCESS_STATUS;

      if (applicantId != null && Number.isFinite(applicantId)) {
        try {
          await applicantHistoryRequests.create({
            applicant_id: applicantId,
            text: `Changes have been made to status updated to "${inProcessLabel}"`,
          });
        } catch (error) {
          console.warn("[AdmissionOffice] In Process history create failed:", error);
        }
      }
    },
    [],
  );

  const handleRejectGeneratedDocument = useCallback(async () => {
    if (!generatedDocument) return;

    const snapshot = taskPrefillSnapshotRef.current;
    const formValues = getValues() as Record<string, unknown>;
    const taskIdValue = String(formValues.task_id ?? snapshot?.payload.task_id ?? "").trim();
    const applicantId = resolveStudentApplicantId();

    dispatch(setIsPreloyder(true));
    try {
      let documentId: number | null =
        typeof generatedDocument.id === "number"
          ? generatedDocument.id
          : typeof generatedDocument.document_id === "number"
            ? generatedDocument.document_id
            : typeof generatedDocument.data?.id === "number"
              ? generatedDocument.data.id
              : null;

      if (documentId == null && generatedDocument.PDFpath) {
        documentId = await lookupDocumentIdByPdfPath(generatedDocument.PDFpath);
      }

      if (documentId == null) {
        notify({
          title: "Delete failed",
          message: "Could not resolve document id. Try again or remove via Admissions Office Database.",
          type: "error",
        });
        return;
      }

      const userOffice = localStorage.getItem("userID") || "without login";
      await admissionDatabaseRequests.deleteCurrentDocument(documentId, userOffice);

      try {
        await resolveAndDeleteApplicantGenerateDoc({
          id: generatedDocument.applicantGenerateDocId,
          applicantId,
          documentSeria: generatedDocument.document_seria,
        });
      } catch (error) {
        console.warn("[AdmissionOffice] applicant-generate-docs delete failed:", error);
      }

      if (taskIdValue) {
        await markTaskInProcessAndClearAttachment(
          taskIdValue,
          applicantId ?? undefined,
        );
        syncTaskPrefillAfterInProcess();
      }

      const inProcessLabel =
        tasksStatusConst.find((item) => item.id === IN_PROCESS_STATUS)?.label ?? IN_PROCESS_STATUS;

      notify({
        title: "Document discarded",
        message: taskIdValue
          ? `Generated document deleted. Task marked as "${inProcessLabel}".`
          : "Generated document has been deleted. You can keep editing.",
        type: "success",
      });
      resetGeneratedState();
    } catch (error) {
      console.error("[AdmissionOffice] Failed to delete generated document:", error);
      notify({
        title: "Delete failed",
        message: "Could not delete the generated document.",
        type: "error",
      });
    } finally {
      dispatch(setIsPreloyder(false));
    }
  }, [
    dispatch,
    generatedDocument,
    getValues,
    lookupDocumentIdByPdfPath,
    markTaskInProcessAndClearAttachment,
    notify,
    resetGeneratedState,
    resolveStudentApplicantId,
    syncTaskPrefillAfterInProcess,
  ]);

  const handleDocumentCheckReject = useCallback(async () => {
    const snapshot = taskPrefillSnapshotRef.current;
    const formValues = getValues() as Record<string, unknown>;
    const taskIdValue = String(formValues.task_id ?? snapshot?.payload.task_id ?? "").trim();
    const applicantId = resolveStudentApplicantId();
    const fullName = String(
      formValues.fullName ?? snapshot?.payload.fullName ?? "",
    ).trim();
    const attachedFile = String(taskPrefillMeta?.attachedFile ?? "").trim();

    if (!taskIdValue) {
      notify({
        title: "Cannot reject",
        message: "Task Id is empty.",
        type: "error",
      });
      return;
    }

    dispatch(setIsPreloyder(true));
    try {
      const result = await executeDocumentCheckReject({
        taskId: taskIdValue,
        applicantId: applicantId ?? undefined,
        fullName: fullName || undefined,
        attachedFile: attachedFile || undefined,
        lookupDocumentIdByPdfPath,
      });

      if (!result.ok) {
        notify({
          title: "Reject failed",
          message: result.message,
          type: "error",
        });
        return;
      }

      notify({
        title: "Task rejected",
        message: result.message,
        type: "info",
      });
      setIsGeneratedPdfPreviewOpen(false);
      syncTaskPrefillAfterRejected();
    } catch (error) {
      console.error("[AdmissionOffice] Document Check reject failed:", error);
      notify({
        title: "Reject failed",
        message: "Unexpected error while rejecting the task.",
        type: "error",
      });
    } finally {
      dispatch(setIsPreloyder(false));
    }
  }, [
    dispatch,
    getValues,
    lookupDocumentIdByPdfPath,
    notify,
    resolveStudentApplicantId,
    syncTaskPrefillAfterRejected,
    taskPrefillMeta?.attachedFile,
  ]);

  const saveTaskWithDocument = useCallback(
    async (
      attachedFile: string,
      options?: {
        navigateOnSuccess?: boolean;
        closePreviewOnSuccess?: boolean;
        showSuccessNotification?: boolean;
      },
    ): Promise<boolean> => {
      const normalizedAttachedFile = attachedFile.trim();
      if (!normalizedAttachedFile) return false;

      const formValues = getValues() as Record<string, unknown>;
      const snapshot = taskPrefillSnapshotRef.current;
      const taskIdValue = String(formValues.task_id ?? snapshot?.payload.task_id ?? "").trim();
      if (!taskIdValue) {
        notify({
          title: "Cannot save document",
          message: "Task Id is empty. Fill it in to attach the document to a task.",
          type: "error",
        });
        return false;
      }

      const nextStatusLabel = tasksStatusConst.find((item) => item.id === NEXT_STATUS)?.label ?? NEXT_STATUS;

      if (!snapshot) {
        console.warn("[AdmissionOffice Approve] Task prefill snapshot missing — diff uses empty baseline.");
      }

      const { applicantPayload, taskPayload, applicantChanges, taskFormChanges } =
        buildApproveUpdatesFromFormDiff({
          formBaseline: snapshot?.formBaseline ?? {},
          formValues,
          attachedFile: normalizedAttachedFile,
          nextStatus: NEXT_STATUS,
          mode: "all",
        });

      const applicantIdRaw =
        formValues.applicant_id ??
        snapshot?.payload.applicant_id ??
        formValues.student_id ??
        snapshot?.payload.student_id;
      const applicantId = applicantIdRaw != null ? Number(applicantIdRaw) : NaN;
      const hasApplicantChanges = Object.keys(applicantPayload).length > 0;

      logApproveChangedFields({
        applicantId: Number.isFinite(applicantId) ? applicantId : "?",
        taskId: taskIdValue,
        applicantPayload,
        taskPayload,
        applicantChanges,
        taskFormChanges,
        nextStatus: NEXT_STATUS,
        attachedFile: normalizedAttachedFile,
      });

      dispatch(setIsPreloyder(true));
      try {
        if (hasApplicantChanges) {
          if (!Number.isFinite(applicantId)) {
            notify({
              title: "Cannot save document",
              message: "Applicant ID is missing. Cannot save applicant field changes.",
              type: "error",
            });
            return false;
          }

          const applicantUpdateResult = await applicantsRequests.update(applicantId, applicantPayload);
          if (applicantUpdateResult == null) {
            notify({
              title: "Save failed",
              message: "Could not update applicant fields.",
              type: "error",
            });
            return false;
          }

          await createApplicantHistory(
            applicantId,
            buildSimpleHistoryText(
              "the student card",
              `Applicant fields were updated from Admissions Office (${Object.keys(applicantPayload).join(", ")})`,
            ),
          );
        }

        await tasksPageRequests.update(taskIdValue, taskPayload);

        if (studentIdFormField) {
          const rawStudentId = formValues[studentIdFormField];
          const parsedStudentId = rawStudentId != null ? Number(rawStudentId) : NaN;
          if (Number.isFinite(parsedStudentId)) {
            await studentsRequests.createHistory({
              student_id: parsedStudentId,
              text: `Changes have been made to status updated to "${nextStatusLabel}"`,
            });
          }
        }

        syncTaskPrefillAfterDocumentCheckSave(normalizedAttachedFile);
        dispatch(setAdmissionFormContext({ attachedFile: normalizedAttachedFile }));

        if (options?.showSuccessNotification !== false) {
          notify({
            title: "Task updated",
            message: hasApplicantChanges
              ? `Task ${taskIdValue} marked as "${nextStatusLabel}", document attached, applicant updated.`
              : `Task ${taskIdValue} marked as "${nextStatusLabel}" and document attached.`,
            type: "success",
          });
        }

        if (options?.closePreviewOnSuccess !== false) {
          setIsGeneratedPdfPreviewOpen(false);
        }

        if (options?.navigateOnSuccess) {
          clearTaskPrefillState();
          navigate("/tasks");
        }

        return true;
      } catch (error) {
        console.error("[AdmissionOffice] Failed to save document to task:", error);
        notify({
          title: "Save failed",
          message: "Could not update the task. Please try again.",
          type: "error",
        });
        return false;
      } finally {
        dispatch(setIsPreloyder(false));
      }
    },
    [
      clearTaskPrefillState,
      dispatch,
      getValues,
      navigate,
      notify,
      studentIdFormField,
      syncTaskPrefillAfterDocumentCheckSave,
    ],
  );

  const approveTaskWithDocument = useCallback(
    async (attachedFile: string) => {
      await saveTaskWithDocument(attachedFile, {
        navigateOnSuccess: true,
        closePreviewOnSuccess: true,
        showSuccessNotification: true,
      });
    },
    [saveTaskWithDocument],
  );

  const onDocumentGenerated = useCallback(
    (rawResponse: unknown) => {
      const parsed = parseGeneratedDocumentResponse(rawResponse);
      if (!parsed) {
        setGeneratedDocument(null);
        setIsGenerated(false);
        setIsGeneratedPdfPreviewOpen(false);
        return;
      }

      setGeneratedDocument(parsed);
      setIsGenerated(true);

      const pdfUrl = parsed.PDFpath ? buildAdmissionPdfUrl(parsed.PDFpath) : null;
      if (!pdfUrl) {
        setIsGeneratedPdfPreviewOpen(true);
        return;
      }

      void (async () => {
        const documentSeria = parsed.document_seria?.trim() ?? "";
        const studentId = resolveStudentApplicantId();

        if (documentType && documentSeria && studentId != null) {
          try {
            const linked = await applicantGenerateDocsRequests.create({
              applicant_id: studentId,
              document_seria: documentSeria,
              document_type: documentType,
            });
            if (linked?.id != null) {
              setGeneratedDocument((prev) =>
                prev ? { ...prev, applicantGenerateDocId: linked.id } : prev,
              );
            }
          } catch (error) {
            console.warn("[AdmissionOffice] applicant-generate-docs create failed:", error);
          }
        }

        const saved = await saveTaskWithDocument(pdfUrl, {
          navigateOnSuccess: false,
          closePreviewOnSuccess: false,
          showSuccessNotification: true,
        });
        if (saved) {
          setIsGeneratedPdfPreviewOpen(true);
        }
      })();
    },
    [documentType, resolveStudentApplicantId, saveTaskWithDocument],
  );

  const handleApproveGeneratedDocument = useCallback(async () => {
    if (!generatedPdfUrl) return;
    await approveTaskWithDocument(generatedPdfUrl);
  }, [approveTaskWithDocument, generatedPdfUrl]);

  /** Generate doc / eye popup: Approve and close (X) only dismiss the preview. */
  const handlePreviewApprove = useCallback(() => {
    setIsGeneratedPdfPreviewOpen(false);
  }, []);

  const handlePreviewReject = useCallback(async () => {
    if (generatedPdfUrl) {
      await handleRejectGeneratedDocument();
      return;
    }

    if (isTaskDocumentCheckReview) {
      await handleDocumentCheckReject();
    }
  }, [generatedPdfUrl, handleDocumentCheckReject, handleRejectGeneratedDocument, isTaskDocumentCheckReview]);

  return {
    isGenerated,
    setIsGenerated,
    generatedDocument,
    generatedPdfUrl,
    taskAttachedPdfUrl,
    previewPdfUrl,
    canOpenPdfPreview,
    canOpenTaskDocumentEye,
    isTaskDocumentCheckReview,
    isGeneratedPdfPreviewOpen,
    setIsGeneratedPdfPreviewOpen,
    generatedPreviewTitle,
    resetGeneratedState,
    setTaskPrefillSnapshot,
    ensureTaskCreatedFromStudentPrefill,
    ensureTaskReadyForDocumentGenerate,
    onDocumentGenerated,
    handleDownloadGeneratedPdf,
    handleApproveGeneratedDocument,
    handleRejectGeneratedDocument,
    handlePreviewApprove,
    handlePreviewReject,
  };
};
